import React, { useState, useEffect } from 'react';
import styled, { useTheme } from 'styled-components';

const OrientationOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => props.$theme?.colors?.background || '#0A0A0A'};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: ${props => props.$theme?.spacing?.large || '24px'};
`;

const RotateIcon = styled.div`
  width: 120px;
  height: 120px;
  margin-bottom: ${props => props.$theme?.spacing?.xlarge || '32px'};
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: rotate 2s ease-in-out infinite;
  transform-origin: center center;

  @keyframes rotate {
    0%, 100% {
      transform: rotate(0deg);
    }
    50% {
      transform: rotate(90deg);
    }
  }

  &::before {
    content: '📱';
    font-size: 120px;
    display: block;
    line-height: 1;
    transform-origin: center center;
  }
`;

const Message = styled.h2`
  color: ${props => props.$theme?.colors?.text || '#FFFFFF'};
  font-size: ${props => props.$theme?.fontSizes?.xlarge || '24px'};
  text-align: center;
  margin-bottom: ${props => props.$theme?.spacing?.medium || '16px'};
  font-weight: 600;
`;

const SubMessage = styled.p`
  color: ${props => props.$theme?.colors?.textSecondary || '#B0B0B0'};
  font-size: ${props => props.$theme?.fontSizes?.medium || '16px'};
  text-align: center;
  max-width: 400px;
  line-height: 1.6;
`;

const OrientationLock = ({ children }) => {
  const theme = useTheme(); // Returns empty object if no ThemeProvider
  const [isPortrait, setIsPortrait] = useState(false);

  const checkOrientation = () => {
    // Check if device is in portrait mode
    // Use window.orientation if available (mobile devices), otherwise use dimensions
    let isPortraitMode = false;
    
    if (window.orientation !== undefined) {
      // Mobile device - use orientation angle
      const angle = Math.abs(window.orientation);
      isPortraitMode = angle === 0 || angle === 180;
    } else {
      // Desktop or device without orientation API - use dimensions
      isPortraitMode = window.innerHeight > window.innerWidth;
    }
    
    setIsPortrait(isPortraitMode);
  };

  useEffect(() => {
    // Check orientation on mount
    checkOrientation();

    // Listen for orientation changes
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);

    // Also check on orientationchange event (for mobile devices)
    const handleOrientationChange = () => {
      setTimeout(checkOrientation, 100); // Small delay to ensure dimensions are updated
    };
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  if (isPortrait) {
    return (
      <OrientationOverlay $theme={theme}>
        <RotateIcon $theme={theme} />
        <Message $theme={theme}>Please Rotate Your Device</Message>
        <SubMessage $theme={theme}>
          BarrelRunner.io requires landscape (horizontal) orientation for the best gaming experience.
          <br />
          <br />
          Please rotate your device to continue playing.
        </SubMessage>
      </OrientationOverlay>
    );
  }

  return children;
};

export default OrientationLock;

